/**
 * @module app.pp
 */

import { autoinject, bindingMode, observable } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { IWizardStep } from 'app/core/components/wizard/interfaces';
import { focusFirst } from 'app/shared/lib/focus';
import { AuthContext } from 'app/um/lib/auth-context';
import { IServiceSearchFilters, LookupService } from 'app/cd/services/lookup-service';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { apiCall } from 'app/shared/lib/essette-api';
import { validate } from 'app/shared/elements/ess-rule';
import { AuthService } from 'app/um/services/auth-service';
import { isNullOrUndefined } from 'app/shared/lib/utilities';
import { getReference } from 'app/shared/models/essette-types';
import { I18N } from 'aurelia-i18n';
import { guid } from 'app/shared/lib/guid';
import * as UM from 'app/um/models/essette-api';
import * as CD from 'app/cd/models/essette-api';

@autoinject()
export class ServiceStep extends ComponentBase implements IWizardStep {

    ////****************** Fields and Properties ************************************////

    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    protected auth: UM.Auth = {};

    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    protected missingRequiredData: string[] = [];

    public canSkip: boolean = false;
    public canContinue: boolean = false;
    public canPerformFinalSubmit: boolean = false;

    public selectedService: CD.Service | null = null;

    @observable
    protected quantityRequested: number | null = 1;

    @observable
    protected chargeRequested: number | null = null;

    @observable
    protected authorizedDate: string | null = null;

    @observable
    protected expirationDate: string | null = null;

    protected validate = validate;
    protected $serviceIsValid: boolean = false;
    protected hasAddCommon: boolean = false;
    protected id: guid = guid();
    protected commonServices: UM.CommonService[] | null = null;
    protected commonDentalServices: UM.CommonService[] | null = null;
    protected dropdownInUse: boolean = false;

    protected selectedModifiers: CD.AuthModifier[] = [];
    public modifierSearchQuery: string | null = null;   // the value of the autocomplete textbox
    private modifierSearchResults: CD.Service[] = [];   // array of search results (for modifiers) from API for autocomplete

    @bindable.string
    public serviceOption: string = 'service';
    protected bodySites: CD.BodySite[] = [];
    protected bodySubSites: CD.BodySubSite[] = [];
    protected selectedBodySite: CD.BodySite | null = null;
    protected selectedBodySubSite: CD.BodySubSite | null = null;

    public serviceSearchQuery: string | null = null;    // the value of the autocomplete text box
    private serviceSearchResults: CD.Service[] = [];    // array of search results (for service) from API for autocomplete

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private $element: Element,
        private readonly context: AuthContext,
        private readonly authService: AuthService,
        private readonly lookupService: LookupService,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        if(!this.auth.id) return;
        // if only dental services are available per auth context settings, default showDental to true
        if(this.context.settings.servicesDental.visible && !this.context.settings.services.visible) this.serviceOption = 'dentalService';
        this.checkPermissions();
        this.context.configure(this.auth.id);
        this.loadLookups();
        this.resetFields();
        this.canContinue = this.missingRequiredData.length === 0;
    }

    ////****************** Events ***************************************************////

    protected async selectService(service: CD.Service): Promise<void> {
        this.selectedService = service;
        this.serviceSearchQuery = null;
        this.serviceSearchResults.splice(0); //clear search results (and thus hide the auto-complete result list)
        this.recalculateMissingData(this.i18n.tr('um:service.$'), true);
    }

    protected async selectModifier(modifier: CD.AuthModifier): Promise<void> {
        if(!modifier) { return; }

        if(!!this.selectedModifiers) {
            this.selectedModifiers.push(modifier);
            this.modifierSearchQuery = null;
            this.modifierSearchResults.splice(0);
        }
    }

    ////****************** Functions ************************************************////

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasAddCommon = this.accountContext.hasPermission('UM.CommonService.Query');
    }

    public skipStep(): void { }

    public continueStep(): void {
        if(!this.selectedService) return;
        const newAuthService: UM.AuthService = { definition: this.selectedService };
        if(this.quantityRequested) {
            newAuthService.quantity = {
                requested: this.quantityRequested
            };
        }
        if(this.chargeRequested) {
            newAuthService.charge = {
                requested: this.chargeRequested.toString()
            };
        }
        if(this.authorizedDate) newAuthService.authorizedDate = this.authorizedDate;
        if(this.expirationDate) newAuthService.expirationDate = this.expirationDate;
        if(this.selectedBodySite) newAuthService.bodySite = getReference(this.selectedBodySite);
        if(this.selectedBodySubSite) newAuthService.bodySubSite = getReference(this.selectedBodySubSite);
        if(this.selectedModifiers.length > 0) {
            newAuthService.modifiers = [] as UM.AuthModifierDetailModel[];
            this.selectedModifiers.forEach(m => {
                newAuthService!.modifiers!.push({ id: m.id });
            });
        }
        this.context.auth.services = [newAuthService];
    }

    public openStep(): void {
        setTimeout(() => focusFirst(this.$element), 250);
    }

    public async closeStep(): Promise<void> { }

    public async finalSubmit(): Promise<void> { }

    protected async toggleDental(): Promise<void> {
        this.resetFields();

        await Promise.all(
            [
                this.loadCommonServices(),
                this.loadLookups()
            ]
        );
    }

    private resetFields(): void {
        this.selectedService = null;
        this.quantityRequested = 1;
        this.chargeRequested = null;
        this.authorizedDate = null;
        this.expirationDate = null;
        this.selectedBodySite = null;
        this.selectedBodySubSite = null;
        this.serviceSearchQuery = null;
        this.serviceSearchResults.splice(0);
        this.modifierSearchQuery = null;
        this.modifierSearchResults.splice(0);
        this.selectedModifiers.splice(0);

        // reset missing fields data
        this.missingRequiredData.splice(0);
        this.missingRequiredData.push(this.i18n.tr('um:service.$'));
        if(this.serviceOption === 'dentalService') {
            if(this.context.settings.servicesDentalAuthDate.reqDataEntry) this.missingRequiredData.push(this.i18n.tr('core:metadata.start.timestamp.$long'));
            if(this.context.settings.servicesDentalExpDate.reqDataEntry) this.missingRequiredData.push(this.i18n.tr('core:metadata.end.timestamp.$long'));
        } else {
            if(this.context.settings.serviceRequestedCharges.reqDataEntry) this.missingRequiredData.push(this.i18n.tr('um:service.charge.requested.$'));
            if(this.context.settings.servicesAuthDate.reqDataEntry) this.missingRequiredData.push(this.i18n.tr('core:metadata.start.timestamp.$long'));
            if(this.context.settings.servicesExpDate.reqDataEntry) this.missingRequiredData.push(this.i18n.tr('core:metadata.end.timestamp.$long'));
        }
    }

    protected async addCommonService(service: UM.CommonService): Promise<void> {
        if(!service.service) return;
        this.selectedService = service.service;
        this.recalculateMissingData(this.i18n.tr('um:service.$'), true);
    }

    protected filterModifiers = (modifier: CD.AuthModifier): boolean => {
        if(!modifier) { return false; }

        return !this.selectedModifiers?.some(m => m.id === modifier.id);
    }

    protected async removeModifier(modifier: CD.AuthModifier): Promise<void> {
        if(!modifier) { return; }
        this.selectedModifiers = this.selectedModifiers.filter(m => m.id !== modifier.id);
    }

    protected clearSelectedService(): void {
        this.selectedService = null;
        this.recalculateMissingData(this.i18n.tr('um:service.$'), false);
    }

    protected quantityRequestedChanged(): void {
        const valid = !isNullOrUndefined(this.quantityRequested) && this.quantityRequested > 0;
        this.recalculateMissingData(this.i18n.tr('um:service.quantity.requested.$long'), valid);
    }

    protected chargeRequestedChanged(): void {
        if(!this.context.settings.serviceRequestedCharges.reqDataEntry) return;
        const valid = !isNullOrUndefined(this.chargeRequested);
        this.recalculateMissingData(this.i18n.tr('um:service.charge.requested.$'), valid);
    }

    protected authorizedDateChanged(): void {
        if(!this.context.settings.servicesAuthDate.reqDataEntry && this.serviceOption === 'service') return;
        if(!this.context.settings.servicesDentalAuthDate.reqDataEntry && this.serviceOption === 'dentalService') return;
        const valid = !isNullOrUndefined(this.authorizedDate)
        this.recalculateMissingData(this.i18n.tr('core:metadata.start.timestamp.$long'), valid);
    }

    protected expirationDateChanged(): void {
        if(!this.context.settings.servicesExpDate.reqDataEntry && this.serviceOption === 'service') return;
        if(!this.context.settings.servicesDentalExpDate.reqDataEntry && this.serviceOption == 'dentalService') return;
        const valid = !isNullOrUndefined(this.expirationDate);
        this.recalculateMissingData(this.i18n.tr('core:metadata.end.timestamp.$long'), valid);
    }

    protected recalculateMissingData(fieldName: string, valid: boolean): void {
        const foundIndex = this.missingRequiredData.findIndex(s => s === fieldName);
        if(valid) {
            if(foundIndex > -1) this.missingRequiredData.splice(foundIndex, 1);
        } else {
            if(foundIndex < 0) this.missingRequiredData.push(fieldName);
        }
        this.canContinue = this.missingRequiredData.length === 0;
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    private async loadLookups() {
        try {
            this.state.beginLoad();

            const includes: string[] = ['service'];

            const showDental = this.serviceOption === 'dentalService' ? true : false;

            const serviceSearchFilters: IServiceSearchFilters = { isDental: showDental };

            const commonServices = await this.authService.loadCommonServices({ all: true }, includes, serviceSearchFilters);

            if(showDental) this.commonDentalServices = commonServices.results;
            else this.commonServices = commonServices.results;

            if(showDental) {
                const bodySiteTypeCode = 'Dental';
                const [bodySites, bodySubSites] = await Promise.all(
                    [
                        this.lookupService.loadBodySites({ all: true }, [], undefined, bodySiteTypeCode),
                        this.lookupService.loadBodySubSites({ all: true }, [], undefined, bodySiteTypeCode)
                    ]
                );

                this.bodySites = bodySites.results;
                this.bodySubSites = bodySubSites.results;
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadCommonServices() {
        try {
            const includes: string[] = ['service'];

            const showDental = this.serviceOption === 'dentalService' ? true : false;

            if(showDental && !this.commonDentalServices) {
                includes.push('service.type.bodySiteType');
                const serviceSearchFilters: IServiceSearchFilters = { isDental: true };
                const commonDentalServices = await this.authService.loadCommonServices({ all: true }, includes, serviceSearchFilters);
                this.commonDentalServices = commonDentalServices.results || [];
            } else if(!this.commonServices) {
                const serviceSearchFilters: IServiceSearchFilters = { isDental: false };
                const commonServices = await this.authService.loadCommonServices({ all: true }, includes, serviceSearchFilters);
                this.commonServices = commonServices.results || [];
            }
        } catch(error) {
            throw error;
        }
    }

    @apiCall
    protected async searchServices(value: string): Promise<void> {
        if(!value) {
            if(this.serviceSearchResults) this.serviceSearchResults = [];
            return;
        }

        const showDental = this.serviceOption === 'dentalService' ? true : false;

        const options: Partial<IQueryLoadOptions> = { top: 30 };
        const includes: string[] = [];
        if(showDental) includes.push('type.bodySiteType');

        try {
            const serviceSearchFilters: IServiceSearchFilters = { isDental: showDental, codeOrDescription: value };
            const response = await this.lookupService.searchServices(undefined, options, includes, true, serviceSearchFilters);
            this.serviceSearchResults = response.results;
        }
        catch(error) {
            throw error;
        }
    }

    @apiCall
    protected async searchModifiers(value: string): Promise<void> {
        if(!value) {
            if(this.modifierSearchResults) this.modifierSearchResults = [];
            return;
        }

        try {
            const response = await this.authService.searchAuthModifiers(value);
            this.modifierSearchResults = response.results;
        }
        catch(error) {
            throw error;
        }
    }

}
